/**
 * How coπ learns by itself (Gordon 2026-11-01: "coπ voice commands need to be
 * trained it seems … can it train itself?"). Pure — shared by the mic, the API
 * routes, the nightly review and the Super Admin card.
 *
 * The layers, cheapest first: rules (lib/copi-commands, free) → what it has
 * LEARNED (phrases taught by people, AI answers nobody undid, name spellings
 * from the nightly review — free, from memory) → the catalogue (free) → the AI
 * (a fraction of a cent) → asking the person.
 *
 * Every action that is stored or replayed passes `cleanCopiAction` — the same
 * whitelist the AI route uses — so nothing learned can do more than the AI
 * itself is allowed to do.
 */

import { phraseKey, type CopiAction, type MediaIntent, type NoteKind, type SnapKind } from './copi-commands';
import { MAIN_VOICE_IDS, type VoiceGender } from './copi-voices';

/** One action coπ can take, as the AI (and memory) describes it. */
export type CopiAiAction =
  | { action: 'navigate'; href: string }
  | ({ action: 'media' } & MediaIntent)
  | { action: 'open'; kind: 'artist' | 'release' | 'track' | 'video'; name: string }
  | { action: 'play' | 'pause' | 'next' | 'prev' | 'restart' | 'back' | 'chat' | 'favorites' | 'close' }
  | { action: 'search'; q: string }
  | { action: 'menu'; open?: boolean }
  /** Bookkeeping camera, a songwriting note, or an edit handed to Copilot.
   *  A note's words and a Copilot request are the speaker's own — never remembered. */
  | { action: 'snap'; kind: SnapKind }
  | { action: 'note'; kind: NoteKind; text: string }
  | { action: 'copilot' }
  | { action: 'epx' }
  | { action: 'voice'; gender?: VoiceGender; random?: boolean; device?: boolean; id?: string }
  | { action: 'unknown' };

/** Which layer answered a command — counted per day for the Super Admin card. */
export type CopiLayer = 'rules' | 'memory' | 'catalogue' | 'ai' | 'unknown';
export const COPI_LAYERS: CopiLayer[] = ['rules', 'memory', 'catalogue', 'ai', 'unknown'];

/** An AI answer is remembered once nobody undid it for this long. */
export const COPI_CONFIRM_MS = 8000;
/** …and is used for EVERYONE once this many different people got the same answer. */
export const COPI_PROMOTE_AT = 3;

const KINDS = ['any', 'track', 'release', 'video', 'playlist', 'artist'];
const OPEN_KINDS = ['artist', 'release', 'track', 'video'];
const SIMPLE = ['play', 'pause', 'next', 'prev', 'back', 'chat', 'favorites', 'close', 'restart'];

const str = (v: unknown, max = 120) => (typeof v === 'string' ? v.replace(/\s+/g, ' ').trim().slice(0, max) : '');

/** An app path coπ may open: internal, plain, short. */
export const copiInternalHref = (href: string) => href === '/' || /^\/[a-z0-9][a-z0-9\-/]{0,79}$/i.test(href);

/** Only allowed actions with sane values — anything else is null. `unknown` passes as itself. */
export function cleanCopiAction(a: unknown, hrefOk: (href: string) => boolean = copiInternalHref): CopiAiAction | null {
  if (!a || typeof a !== 'object') return null;
  const x = a as Record<string, unknown>;
  const action = str(x.action, 20);
  if (SIMPLE.includes(action)) return { action } as CopiAiAction;
  if (action === 'unknown') return { action };
  if (action === 'menu') return typeof x.open === 'boolean' ? { action, open: x.open } : { action };
  if (action === 'snap') {
    const kind = str(x.kind, 10);
    return { action, kind: (['receipt', 'invoice', 'document'].includes(kind) ? kind : 'receipt') as SnapKind };
  }
  if (action === 'note') {
    const kind = str(x.kind, 10);
    return { action, kind: (['idea', 'lyrics', 'concept'].includes(kind) ? kind : 'idea') as NoteKind, text: str(x.text, 2000) };
  }
  if (action === 'copilot' || action === 'epx') return { action };
  if (action === 'voice') {
    const r: { action: 'voice'; gender?: VoiceGender; random?: boolean; device?: boolean; id?: string } = { action };
    const g = str(x.gender, 10), id = str(x.id, 40);
    if (['female', 'male', 'neutral'].includes(g)) r.gender = g as VoiceGender;
    if (x.random === true) r.random = true;
    if (x.device === true) r.device = true;
    if (MAIN_VOICE_IDS.has(id)) r.id = id;
    return r;
  }
  if (action === 'navigate') {
    const href = str(x.href, 80);
    return href && hrefOk(href) ? { action, href } : null;
  }
  if (action === 'search') { const q = str(x.q); return q ? { action, q } : null; }
  if (action === 'open') {
    const kind = str(x.kind, 10), name = str(x.name);
    return OPEN_KINDS.includes(kind) && name ? { action, kind: kind as 'artist', name } : null;
  }
  if (action === 'media') {
    const kind = (KINDS.includes(str(x.kind, 10)) ? str(x.kind, 10) : 'any') as MediaIntent['kind'];
    const r: { action: 'media' } & MediaIntent = { action, kind };
    for (const f of ['q', 'artist', 'genre'] as const) { const v = str(x[f]); if (v) r[f] = v; }
    const bpm = Number(x.bpm);
    if (Number.isFinite(bpm) && bpm >= 40 && bpm <= 220) r.bpm = Math.round(bpm);
    if (x.random === true) r.random = true;
    return r;
  }
  return null;
}

/** A stable fingerprint of an action — "the same answer" for the 3-people rule. */
export function copiActionSig(a: CopiAiAction): string {
  const sorted = Object.keys(a).sort().map(k => [k, (a as Record<string, unknown>)[k]]);
  return JSON.stringify(sorted).toLowerCase();
}

/** "sv-SE" → "sv", "zh-TW" → "zh-tw" (Traditional stays apart). */
export function copiLang(locale: string): string {
  const l = (locale || '').toLowerCase().replace('_', '-');
  return l === 'zh-tw' ? 'zh-tw' : l.split('-')[0] || 'en';
}

/** What learned memory is keyed by: language + the phrase's key. */
export const copiMemoryKey = (lang: string, key: string) => `${lang}|${key}`;

/** A remembered answer for these words — same language first, then any. */
export function lookupCopiMemory(memory: Map<string, CopiAiAction>, lang: string, said: string): CopiAiAction | null {
  const key = phraseKey(said);
  if (!key) return null;
  return memory.get(copiMemoryKey(lang, key)) || memory.get(copiMemoryKey('*', key)) || null;
}

type Family = 'media' | 'page' | 'search' | 'other';
function familyOf(a: CopiAiAction): Family {
  if (a.action === 'media') return 'media';
  if (a.action === 'navigate' || a.action === 'open') return 'page';
  if (a.action === 'search') return 'search';
  return 'other';
}
function familyOfCommand(c: CopiAction): Family {
  if (c.type === 'media') return 'media';
  if (c.type === 'navigate' || c.type === 'current') return 'page';
  if (c.type === 'search') return 'search';
  return 'other';
}

/**
 * Did the next command (within COPI_CONFIRM_MS) undo or replace what the AI
 * did? Then the AI's answer is NOT remembered. Undo: back, close, pause,
 * stop, the opposite control. Replace: asking again for the same kind of
 * thing (another "play …", another page) or words coπ couldn't place.
 * Carrying on (next track, favorites, chat) is not a correction.
 */
export function contradictsCopi(learned: CopiAiAction, next: CopiAction): boolean {
  if (next.type === 'back' || next.type === 'close' || next.type === 'stop') return true;
  if (next.type === 'pause' && learned.action !== 'pause') return true;
  if (next.type === 'search' && next.loose) return true;
  const undo: Record<string, string> = { pause: 'play', play: 'pause', next: 'prev', prev: 'next', menu: 'menu', favorites: 'favorites' };
  if (undo[learned.action] === next.type) return true;
  const f = familyOf(learned);
  return f !== 'other' && f === familyOfCommand(next);
}

// ── Name spellings ("leaf nut" → Leafnuts) ─────────────────────────────────

/** Words that are commands, never a misheard name — an alias may not be one. */
const RESERVED = new Set([
  'play', 'pause', 'stop', 'next', 'previous', 'back', 'close', 'open', 'show', 'search', 'find', 'menu', 'chat', 'home', 'skip',
  'download', 'tuner', 'favorites', 'hero', 'music', 'song', 'track', 'album', 'video', 'playlist', 'random', 'artist', 'release',
  'spela', 'pausa', 'nasta', 'stang', 'oppna', 'visa', 'sok', 'hem', 'musik', 'lat', 'meny',
  'pon', 'para', 'siguiente', 'anterior', 'cierra', 'abre', 'busca', 'inicio', 'musica', 'cancion', 'tema',
  'joue', 'mets', 'suivant', 'precedent', 'ferme', 'ouvre', 'cherche', 'accueil', 'musique', 'chanson', 'morceau',
  'no', 'yes', 'non', 'si', 'oui', 'ja', 'nej', 'the', 'a', 'and', 'de', 'la', 'le', 'el',
]);

/** May `spoken` stand for a name? Long enough, not a command word. */
export function aliasKeyOk(spoken: string): boolean {
  const key = phraseKey(spoken);
  if (key.length < 3 || key.split(' ').length > 5) return false;
  return !key.split(' ').every(w => RESERVED.has(w) || w.length < 2);
}

const bigrams = (s: string) => {
  const t = s.replace(/[^a-z0-9]/g, '');
  const out: string[] = [];
  for (let i = 0; i < t.length - 1; i++) out.push(t.slice(i, i + 2));
  return out;
};
/** Do the two at least SOUND alike on paper? ("lift nuts" ≈ "Leafnuts", "pizza" ≠ "Leafnuts".) */
export function aliasPlausible(spoken: string, name: string): boolean {
  const a = bigrams(phraseKey(spoken)), b = bigrams(phraseKey(name));
  if (!a.length || !b.length) return false;
  const pool = [...b];
  let hits = 0;
  for (const g of a) { const i = pool.indexOf(g); if (i >= 0) { hits++; pool.splice(i, 1); } }
  return (2 * hits) / (a.length + b.length) >= 0.3;
}

/**
 * Spell catalogue names the catalogue's way before matching: every run of up
 * to five words whose key is a learned spelling is replaced by the name
 * ("play leaf nut" → "play Leafnuts"). The longest run wins.
 */
export function applyCopiAliases(text: string, aliases: Map<string, string>): string {
  if (!aliases.size || !text) return text;
  const tokens = text.split(/\s+/).filter(Boolean);
  const out: string[] = [];
  for (let i = 0; i < tokens.length;) {
    let hit = '';
    let len = 0;
    for (let n = Math.min(5, tokens.length - i); n >= 1; n--) {
      const name = aliases.get(phraseKey(tokens.slice(i, i + n).join(' ')));
      if (name) { hit = name; len = n; break; }
    }
    if (hit) { out.push(hit); i += len; } else { out.push(tokens[i]); i++; }
  }
  return out.join(' ');
}

/** The same spellings applied to what "play …" asked for. */
export function aliasIntent<T extends MediaIntent>(intent: T, aliases: Map<string, string>): T {
  if (!aliases.size) return intent;
  const r = { ...intent };
  for (const f of ['q', 'artist', 'genre'] as const) { const v = r[f]; if (v) r[f] = applyCopiAliases(v, aliases); }
  return r;
}

/** Words of a command a person said, as a readable line ("media · track · Stocktown"). */
export function describeCopiAction(a: CopiAiAction | null): string {
  if (!a) return '—';
  const { action, ...rest } = a as Record<string, unknown> & { action: string };
  const vals = Object.entries(rest).filter(([, v]) => v !== undefined && v !== '').map(([k, v]) => (v === true ? k : `${k}: ${v}`));
  return [action, ...vals].join(' · ');
}
